import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inrShort, budgetRange } from "@/lib/format";
import { Plus, X, Phone } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal_sent", label: "Proposal Sent" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

const SOURCES = ["Instagram", "Referral", "Google", "Housing", "Walk-in"];

function LeadCard({ lead, onStatusChange }) {
  return (
    <div className="bg-white border border-anvi-200 rounded-md p-3 hover:border-anvi-terracotta hover:shadow-sm transition-all cursor-pointer" data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="font-medium text-anvi-900 text-sm truncate">{lead.client_name}</div>
          <div className="text-xs text-anvi-500 flex items-center gap-1 mt-0.5">
            <Phone size={10} strokeWidth={1.5} /> {lead.phone}
          </div>
        </div>
        <div className="shrink-0 text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full bg-anvi-terracotta/10 text-anvi-terracotta" data-testid={`lead-score-${lead.id}`}>
          {lead.score}
        </div>
      </div>
      <div className="text-xs text-anvi-500 mb-2">{lead.property_type}</div>
      <div className="text-sm font-medium tabular-nums text-anvi-800 mb-3">{budgetRange(lead.budget_min, lead.budget_max)}</div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider bg-anvi-100 text-anvi-700">{lead.source}</span>
        <select
          value={lead.status}
          onChange={(e) => onStatusChange(lead.id, e.target.value)}
          data-testid={`lead-status-select-${lead.id}`}
          className="text-xs border border-anvi-200 bg-white rounded-sm px-1.5 py-0.5 focus:outline-none focus:border-anvi-terracotta"
        >
          {COLUMNS.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function CreateLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    client_name: "", phone: "", email: "", source: "Instagram",
    property_type: "3BHK Apartment", budget_min: 500000, budget_max: 1500000, notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.leadCreate({
        ...form,
        budget_min: Number(form.budget_min),
        budget_max: Number(form.budget_max),
      });
      toast.success(`Lead created · score ${res.data.score}`);
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error("Could not create lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-anvi-900/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg border border-anvi-200 shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="create-lead-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-anvi-200">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">New Lead</div>
            <div className="font-heading text-xl text-anvi-900 mt-0.5">Capture prospect</div>
          </div>
          <button onClick={onClose} className="text-anvi-500 hover:text-anvi-900 p-1" data-testid="close-lead-modal"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Client name</label>
              <input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                     data-testid="new-lead-name" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Phone</label>
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     data-testid="new-lead-phone" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Source</label>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                      data-testid="new-lead-source" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
                {SOURCES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Property Type</label>
              <input value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                     className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Budget Min (₹)</label>
              <input type="number" value={form.budget_min} onChange={(e) => setForm({ ...form, budget_min: e.target.value })}
                     data-testid="new-lead-budget-min" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:border-anvi-terracotta" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Budget Max (₹)</label>
              <input type="number" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })}
                     data-testid="new-lead-budget-max" className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:border-anvi-terracotta" />
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase text-anvi-500 block mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full border border-anvi-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-anvi-terracotta" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-anvi-700 hover:bg-anvi-100 rounded-md transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-new-lead" className="px-4 py-2 text-sm bg-anvi-terracotta text-white hover:bg-anvi-terracotta-dark rounded-md transition-colors disabled:opacity-60">
              {saving ? "Creating…" : "Create Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = () => api.leadsList().then((r) => setLeads(r.data));

  useEffect(() => { load(); }, []);

  const onStatusChange = async (id, status) => {
    try {
      await api.leadUpdate(id, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Lead moved");
    } catch {
      toast.error("Update failed");
    }
  };

  const grouped = COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: leads.filter((l) => l.status === c.key) }), {});
  const total = leads.length;
  const won = leads.filter((l) => l.status === "won").length;
  const winRate = total ? Math.round((won / total) * 100) : 0;

  return (
    <div className="px-10 pt-4 pb-10 animate-fade-in" data-testid="leads-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-anvi-500 mb-2">CRM</div>
          <h1 className="font-heading text-4xl text-anvi-900">Leads</h1>
          <p className="text-sm text-anvi-500 mt-2">{total} total · {winRate}% win rate · drag through the pipeline via status</p>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="new-lead-btn"
                className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors">
          <Plus size={16} strokeWidth={1.5} /> New Lead
        </button>
      </div>

      <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4" data-testid="leads-kanban">
        {COLUMNS.map((c) => (
          <div key={c.key} className="w-72 shrink-0 bg-anvi-100 rounded-md p-3" data-testid={`kanban-column-${c.key}`}>
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-600 font-medium">{c.label}</div>
              <div className="text-xs font-semibold text-anvi-500 tabular-nums">{grouped[c.key].length}</div>
            </div>
            <div className="space-y-2 min-h-[80px]">
              {grouped[c.key].map((l) => (
                <LeadCard key={l.id} lead={l} onStatusChange={onStatusChange} />
              ))}
              {grouped[c.key].length === 0 && (
                <div className="text-xs text-anvi-400 py-6 text-center border border-dashed border-anvi-200 rounded-md">No leads</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && <CreateLeadModal onClose={() => setShowModal(false)} onCreated={(lead) => setLeads((p) => [lead, ...p])} />}
    </div>
  );
}
